/**
 * PRP-WEB-1 W3 / PRP-WEB-2 W2: the full-screen floor plan editor, `/project/:id/plan`.
 *
 * WEB-1: zoom and pan, click a room to fix it in the panel, Undo/Redo, one explicit Save that
 * sends only the changed rooms and shows how the linked chat estimates followed.
 * WEB-2: grab a room, wall, door or window and drag it (`useCanvasEditor` + `EditController`);
 * right-click menus (long-press on touch); keys; the split tool; subroom groups with their
 * order; the Problems strip; a picture to trace over. Every edit is one undo step. Nothing saves
 * until Save, and leaving with unsaved changes asks first.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Redo2, Undo2, Save, RotateCw, Scissors } from 'lucide-react';
import { along, chamferCorner, edgeOf, findRoom, type Plan } from '@sketch-core';
import useProjectStore from '../../stores/projectStore';
import { ProjectOverviewAPI } from '../../lib/projectOverviewApi';
import { Button } from '../components/ui/button';
import { Banner } from '../components/ui/banner';
import { projectLabel } from '../components/EstimateCard';
import { PlanViewport } from '../components/plan/PlanViewport';
import { RoomPanel } from '../components/plan/RoomPanel';
import { fitViewBox, planMetrics, type ViewBox } from '../components/plan/viewBox';
import { describeSave } from '../components/plan/saveResult';
import { useEditPlan } from '../components/plan/editor/useEditPlan';
import { useCanvasEditor } from '../components/plan/editor/useCanvasEditor';
import { useLeaveGuard } from '../components/plan/editor/useLeaveGuard';
import { EditorOverlay } from '../components/plan/editor/EditorOverlay';
import { ContextMenu } from '../components/plan/menus/ContextMenu';
import { PromptDialog } from '../components/plan/menus/PromptDialog';
import { SubroomList } from '../components/plan/groups/SubroomList';
import { ProblemsStrip } from '../components/plan/problems/ProblemsStrip';
import { moveInside, nudgeApart, planProblems } from '../components/plan/problems/problems';
import { useUnderlay } from '../components/plan/underlay/useUnderlay';
import { UnderlayControls, UnderlayImage } from '../components/plan/underlay/UnderlayControls';
import type { PromptSpec } from '../components/plan/menus/menuModel';

export default function V2PlanPage() {
  const { id = '' } = useParams();
  const getProjectById = useProjectStore((s) => s.getProjectById);
  const [projectName, setProjectName] = useState<string>(() => projectLabel(null));
  const core = useEditPlan(id);
  const { status, setStatus, loaded, plan, sel, setSel, groups } = core;
  const [view, setView] = useState<ViewBox | null>(null);
  const [highlight, setHighlight] = useState<number | null>(null);
  const [boxW, setBoxW] = useState(1000);
  const box = useRef<HTMLDivElement>(null);
  const promptRef = useRef<(p: PromptSpec) => void>(() => undefined);

  useEffect(() => {
    let off = false;
    getProjectById(id).then((p) => { if (!off && p) setProjectName(projectLabel(p)); }).catch(() => undefined);
    return () => { off = true; };
  }, [id, getProjectById]);

  // B-7561: how many chat estimates follow this plan (the overview's link state), for the save strip.
  const [linked, setLinked] = useState<number | null>(null);
  useEffect(() => {
    let off = false;
    setLinked(null);
    ProjectOverviewAPI.getOverview(id)
      .then((o) => { if (!off) setLinked(o.estimates.filter((e) => e.follows_plan).length); })
      .catch(() => undefined);
    return () => { off = true; };
  }, [id]);

  const reload = (keepView: boolean) => {
    core.load().then((L) => { if (!keepView && L?.plan.rooms.length) setView(fitViewBox(L.plan)); }).catch(() => setStatus('error'));
    core.setResult(null);
  };
  // Load once per project; `reload` is rebuilt every render on purpose (it reads the latest state).
  useEffect(() => { setStatus('loading'); reload(false); }, [id]);

  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setBoxW(el.clientWidth || 1000));
    ro.observe(el);
    return () => ro.disconnect();
  }, [status]);

  const fit = useMemo(() => (loaded?.plan.rooms.length ? fitViewBox(loaded.plan) : null), [loaded]);
  const U = useUnderlay({ projectId: id, plan, saved: core.savedUnderlay, fromServer: core.extrasUnderlay, prompt: (p) => promptRef.current(p) });

  const chamfer = (rid: string, vid: number, end: 'start' | 'end') => {
    const r = plan && findRoom(plan, rid);
    const i = r ? r.corners.findIndex((c) => c.id === vid) : -1;
    if (!r || i < 0) return 'That wall is gone.';
    return core.act((p) => chamferCorner(p, rid, end === 'start' ? i : (i + 1) % r.corners.length));
  };
  const ed = useCanvasEditor(core, { view, fit, setView, underlay: U, chamfer });
  promptRef.current = ed.setPrompt;

  const dirty = core.pending.length > 0 || core.deleted.length > 0 || core.groupsDirty || U.dirty;
  const changeCount = core.pending.length + core.deleted.length;

  // Leaving with unsaved edits asks first: tab close/reload, browser Back, and every in-app
  // navigation (links and navigate(), e.g. the top bar's project switcher) (review W5).
  useLeaveGuard(dirty);

  // A short note (a refusal, "Split.", "Copied…") fades on its own.
  useEffect(() => {
    if (!core.note) return undefined;
    const t = setTimeout(() => core.setNote(null), 4500);
    return () => clearTimeout(t);
  }, [core.note, core]);

  const problems = useMemo(() => (plan ? planProblems(plan, groups) : []), [plan, groups]);
  const problemRooms = useMemo(() => new Set(problems.filter((p) => p.kind === 'overlap' || p.kind === 'closet-outside').flatMap((p) => [p.rid, ...(p.kind === 'overlap' ? [p.other] : [])])), [problems]);

  const roomId = sel?.rid ?? null;
  const room = plan && roomId ? findRoom(plan, roomId) : undefined;
  const px = view ? view.w / Math.max(200, boxW) : 0.05;

  const overlay = plan ? (
    <>
      {room && sel?.type === 'room' && <WallNumbers plan={plan} rid={room.id} highlight={highlight} />}
      <EditorOverlay plan={plan} sel={sel} hover={ed.hover} frame={ed.frame} groups={groups} problemRooms={problemRooms}
        splitLine={ed.splitLine} calibrate={U.line} px={px} />
    </>
  ) : null;

  const savedText = core.result ? describeSave(core.result, linked) : null;
  const saveLabel = core.saving ? 'Saving…' : dirty ? `Save${changeCount > 1 ? ` (${changeCount} rooms)` : ''}` : core.result ? 'Saved' : 'Save';

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--bg-0)]" data-testid="plan-page">
      <header className="flex flex-wrap items-center gap-2 border-b border-[var(--bd)] bg-[var(--bg-1)] px-3 py-2 sm:px-4">
        <Link to={`/project/${encodeURIComponent(id)}`} aria-label="Back to the project"
          className="grid h-8 w-8 place-items-center rounded-[var(--r-md)] text-[var(--tx-mid)] hover:bg-[var(--bg-3)] hover:text-[var(--tx-hi)] [&_svg]:h-4 [&_svg]:w-4">
          <ArrowLeft />
        </Link>
        <nav aria-label="Breadcrumb" className="flex min-w-0 flex-1 items-center gap-1.5 text-[12.5px] text-[var(--tx-lo)]">
          <Link to="/" className="hover:text-[var(--tx-hi)] max-sm:hidden" data-testid="plan-crumb-estimates">Estimates</Link>
          <span aria-hidden className="max-sm:hidden">/</span>
          <Link to={`/project/${encodeURIComponent(id)}`} className="truncate hover:text-[var(--tx-hi)]" data-testid="plan-project-link">{projectName}</Link>
          <span aria-hidden>/</span>
          <span className="shrink-0 font-[600] text-[var(--tx-hi)]">Floor plan</span>
        </nav>
        <div className="flex items-center gap-1.5">
          <Button variant={ed.tool === 'split' ? 'primary' : 'secondary'} size="md" data-testid="plan-split-tool" aria-pressed={ed.tool === 'split'}
            title="Split a room in two: click a wall where the line should go" disabled={status !== 'ready'}
            onClick={() => ed.setTool(ed.tool === 'split' ? null : 'split')}><Scissors />Split</Button>
          <Button variant="icon" size="md" aria-label="Undo" title="Undo (Ctrl+Z)" disabled={!core.history.canUndo} onClick={core.undo}><Undo2 /></Button>
          <Button variant="icon" size="md" aria-label="Redo" title="Redo (Ctrl+Y)" disabled={!core.history.canRedo} onClick={core.redo}><Redo2 /></Button>
          <Button variant="primary" size="md" data-testid="plan-save" disabled={!dirty || core.saving}
            onClick={() => core.save(U.saved, U.dirty)}><Save />{saveLabel}</Button>
        </div>
      </header>

      {(core.conflict || core.saveError || savedText || core.note) && (
        <div className="border-b border-[var(--bd)] bg-[var(--bg-1)] px-3 py-2 sm:px-4" aria-live="polite">
          {core.conflict && (
            <Banner tone="warn">
              <div className="flex flex-wrap items-center gap-3" data-testid="plan-conflict">
                <span>This plan changed on another device. Reload to see it.</span>
                <Button size="sm" onClick={() => reload(true)}><RotateCw />Reload</Button>
              </div>
            </Banner>
          )}
          {core.saveError && <Banner tone="warn"><span data-testid="plan-save-error">{core.saveError}</span></Banner>}
          {core.note && <div data-testid="plan-note" className="text-[12.5px] text-[var(--tx-mid)]">{core.note}</div>}
          {savedText && (
            <Banner tone="ok">
              <div data-testid="plan-save-result" className="space-y-1">
                <div className="font-[600]">{savedText.headline}</div>
                {savedText.lines.map((l, i) => <div key={i}>{l}</div>)}
              </div>
            </Banner>
          )}
        </div>
      )}

      {status === 'ready' && plan && (
        <ProblemsStrip plan={plan} problems={problems} onSelect={(rid) => setSel({ type: 'room', rid })}
          onNudge={(rid, other) => core.act((p) => nudgeApart(p, rid, other))}
          onMoveInside={(rid, host) => core.act((p) => moveInside(p, rid, host))} />
      )}

      {/* Under 640 px the panel is a bottom sheet below the plan, which shrinks (and re-fits) above it. */}
      <div className="relative flex min-h-0 flex-1 max-sm:flex-col">
        <div ref={box} className="relative min-h-0 min-w-0 flex-1">
          {status === 'loading' && <Centered>Loading the floor plan…</Centered>}
          {status === 'error' && <Centered>Could not load the floor plan. <Button size="sm" onClick={() => reload(true)}>Try again</Button></Centered>}
          {status === 'empty' && (
            <Centered>
              No floor plan yet. Rooms sketched on the phone or added in chat show here.
              {loaded?.undrawn.length ? <UndrawnList rooms={loaded.undrawn} /> : null}
            </Centered>
          )}
          {status === 'ready' && plan && view && fit && (
            <PlanViewport plan={plan} view={view} fit={fit} onViewChange={setView}
              selectedRoomId={roomId} onRoomClick={(rid) => { setSel({ type: 'room', rid }); setHighlight(null); }}
              onBackgroundClick={() => setSel(null)} overlay={overlay} editor={ed.editor} cursor={ed.cursor}
              underlay={U.u ? <UnderlayImage u={U.u} /> : undefined} />
          )}
          {status === 'ready' && loaded && (loaded.undrawn.length > 0 || loaded.banners.length > 0) && (
            <div className="pointer-events-none absolute bottom-3 left-3 max-w-[min(420px,calc(100%-24px))] space-y-1 rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-2)]/95 p-2 text-[11.5px] text-[var(--tx-lo)]">
              {loaded.undrawn.length > 0 && <UndrawnList rooms={loaded.undrawn} />}
              {loaded.banners.map((b, i) => <div key={i}>{b.room}: {b.text}</div>)}
            </div>
          )}
          {status === 'ready' && (
            <UnderlayControls u={U.u} mode={U.mode} busy={U.busy} error={U.error} onPick={U.pick} onMode={U.setMode}
              onOpacity={U.opacity} onRemove={U.remove} />
          )}
        </div>
        {room && plan && loaded && (
          <RoomPanel plan={plan} room={room} editability={core.editability(room.id)}
            onAction={(a) => { const m = core.act(a); return m ? { ok: false, message: m } : { ok: true }; }}
            onWallLength={(vid, ft, o) => core.wallLength(room.id, vid, ft, o)} onHighlightWall={setHighlight}
            onClose={() => { setSel(null); setHighlight(null); }}
            extra={<SubroomList plan={plan} groups={groups} rid={room.id} editable={core.editable} onAct={core.act}
              onGroups={core.changeGroups} onSelect={(rid) => setSel({ type: 'room', rid })} />} />
        )}
      </div>

      {ed.menu && <ContextMenu x={ed.menu.x} y={ed.menu.y} title={ed.menuTitle} items={ed.menuItems} onClose={ed.closeMenu} />}
      {ed.prompt && <PromptDialog spec={ed.prompt} onClose={() => ed.setPrompt(null)} />}
    </div>
  );
}

/** The panel's wall numbers while one of its fields is focused (WEB-1 W3.3). */
function WallNumbers({ plan, rid, highlight }: { plan: Plan; rid: string; highlight: number | null }) {
  const room = findRoom(plan, rid);
  if (!room) return null;
  const { span } = planMetrics(plan);
  const fs = Math.max(0.55, span / 42) * 0.7;
  return (
    <g pointerEvents="none" data-testid="plan-wall-numbers">
      {room.corners.map((c, i) => {
        const E = edgeOf(room, c.id);
        if (!E) return null;
        const m = along(E.a, E.b, Math.hypot(E.b.x - E.a.x, E.b.y - E.a.y) / 2);
        const on = highlight === c.id;
        return (
          <g key={c.id}>
            {on && <line data-testid="plan-wall-highlight" x1={E.a.x} y1={E.a.y} x2={E.b.x} y2={E.b.y}
              stroke="var(--accent)" strokeWidth={Math.max(0.25, span / 120)} strokeLinecap="round" />}
            <circle cx={m.x} cy={m.y} r={fs * 0.8} fill={on ? 'var(--accent)' : 'var(--bg-3)'} stroke="var(--accent)" strokeWidth={fs * 0.08} />
            <text x={m.x} y={m.y} fontSize={fs} textAnchor="middle" dominantBaseline="central"
              fill={on ? 'var(--accent-tx)' : 'var(--tx-hi)'} fontWeight={700}>{i + 1}</text>
          </g>
        );
      })}
    </g>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="grid h-full place-items-center p-6 text-center text-[13px] text-[var(--tx-lo)]"><div className="space-y-3">{children}</div></div>;
}

function UndrawnList({ rooms }: { rooms: { id: string; name: string }[] }) {
  return (
    <div data-testid="plan-undrawn">
      Not drawn (no shape yet, not editable): {rooms.map((r) => r.name).join(', ')}
    </div>
  );
}
